import axios from 'axios';
import { apiFetch, useBackendAuth } from './apiClient';
import { listPaidChargesInRange, listStripeInvoicesIssuedInRange } from './stripeApi';
import xeroAuth from './xeroAuth';
import type { AppSettings, ChannelIncomePoint, ChannelIncomeSummary } from '../settings/types';

export type Granularity = 'day' | 'week' | 'month';
export type CompareMode = 'none' | 'prior_period' | 'prior_year';

export type IncomePeriodSnapshot = {
  from: string;
  to: string;
  totalPaid: number;
  totalInvoiced: number;
  series: ChannelIncomePoint[];
  channels: ChannelIncomeSummary[];
  total: number;
};

export type IncomeReport = IncomePeriodSnapshot & {
  currency: string;
  granularity: Granularity;
  compareMode: CompareMode;
  includeInvoiced: boolean;
  comparison: (IncomePeriodSnapshot & { mode: CompareMode }) | null;
  yoy: IncomePeriodSnapshot | null;
};

export type IncomeReportQuery = {
  from: string;
  to: string;
  granularity?: Granularity;
  compare?: CompareMode;
  includeInvoiced?: boolean;
  /** @deprecated use compare: 'prior_year' */
  yoy?: boolean;
};

function parseXeroDateMs(s?: string | null): number {
  if (!s) {
    return 0;
  }
  const m = /\/Date\((\d+)/.exec(s);
  if (m) {
    return parseInt(m[1], 10);
  }
  return new Date(s).getTime();
}

function enumerateDays(from: Date, to: Date): string[] {
  const keys: string[] = [];
  const cur = new Date(from);
  cur.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  while (cur <= end) {
    keys.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return keys;
}

function enumerateWeeks(from: Date, to: Date): string[] {
  const keys: string[] = [];
  const cur = new Date(from);
  cur.setHours(0, 0, 0, 0);
  const end = new Date(to);
  end.setHours(0, 0, 0, 0);
  while (cur <= end) {
    keys.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 7);
  }
  return keys;
}

function enumerateMonths(from: Date, to: Date): string[] {
  const keys: string[] = [];
  const cur = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(to.getFullYear(), to.getMonth(), 1);
  while (cur <= end) {
    keys.push(cur.toISOString().slice(0, 10));
    cur.setMonth(cur.getMonth() + 1);
  }
  return keys;
}

function bucketKeys(from: Date, to: Date, granularity: Granularity): string[] {
  if (granularity === 'month') {
    return enumerateMonths(from, to);
  }
  if (granularity === 'week') {
    return enumerateWeeks(from, to);
  }
  return enumerateDays(from, to);
}

function bucketKeyForTimestamp(ts: number, keys: string[], granularity: Granularity): string | null {
  const iso = new Date(ts * 1000).toISOString().slice(0, 10);
  if (granularity === 'day') {
    return keys.includes(iso) ? iso : null;
  }
  if (granularity === 'month') {
    const monthKey = iso.slice(0, 7);
    return keys.find((k) => k.slice(0, 7) === monthKey) ?? null;
  }
  for (let i = keys.length - 1; i >= 0; i--) {
    if (iso >= keys[i]) {
      return keys[i];
    }
  }
  return keys[0] ?? null;
}

function emptySeries(keys: string[]): Record<string, ChannelIncomePoint> {
  const out: Record<string, ChannelIncomePoint> = {};
  for (const date of keys) {
    out[date] = {
      date,
      stripePaid: 0,
      stripeInvoiced: 0,
      xeroPaid: 0,
      xeroInvoiced: 0,
      totalPaid: 0,
      totalInvoiced: 0,
      stripe: 0,
      xero: 0,
      meta: 0,
      total: 0,
    };
  }
  return out;
}

function finalizePoint(p: ChannelIncomePoint): ChannelIncomePoint {
  const stripePaid = p.stripePaid ?? p.stripe ?? 0;
  const xeroPaid = p.xeroPaid ?? p.xero ?? 0;
  const stripeInvoiced = p.stripeInvoiced ?? 0;
  const xeroInvoiced = p.xeroInvoiced ?? 0;
  p.stripePaid = stripePaid;
  p.xeroPaid = xeroPaid;
  p.stripeInvoiced = stripeInvoiced;
  p.xeroInvoiced = xeroInvoiced;
  p.totalPaid = stripePaid + xeroPaid;
  p.totalInvoiced = stripeInvoiced + xeroInvoiced;
  p.stripe = stripePaid;
  p.xero = xeroPaid;
  p.total = p.totalPaid;
  return p;
}

function trendPct(current: number, prior: number): number | null {
  if (prior === 0) {
    return current === 0 ? null : 100;
  }
  return Math.round(((current - prior) / prior) * 1000) / 10;
}

function shiftYearBack(d: Date): Date {
  const copy = new Date(d);
  copy.setFullYear(copy.getFullYear() - 1);
  return copy;
}

function shiftPriorPeriod(from: Date, to: Date): { from: Date; to: Date } {
  const spanDays = Math.ceil((to.getTime() - from.getTime()) / 86_400_000) + 1;
  const priorTo = new Date(from);
  priorTo.setDate(priorTo.getDate() - 1);
  const priorFrom = new Date(priorTo);
  priorFrom.setDate(priorFrom.getDate() - (spanDays - 1));
  return { from: priorFrom, to: priorTo };
}

async function fetchLocalXeroPaid(fromTs: number, toTs: number): Promise<Array<{ created: number; amount: number }>> {
  if (!xeroAuth.isAuthenticated()) {
    await xeroAuth.ensureValidAccessToken().catch(() => undefined);
  }
  const token = xeroAuth.getAccessToken();
  const tenantId = xeroAuth.getTenantId();
  if (!token || !tenantId) {
    return [];
  }

  const paidAtSeconds = (inv: { Date?: string; FullyPaidOnDate?: string }) => {
    const ms = parseXeroDateMs(inv.FullyPaidOnDate) || parseXeroDateMs(inv.Date);
    return ms ? Math.floor(ms / 1000) : 0;
  };

  const out: Array<{ created: number; amount: number }> = [];
  const where = encodeURIComponent('Status=="PAID"&&Type=="ACCREC"');

  for (let page = 1; page <= 30; page++) {
    const { data } = await axios.get<{
      Invoices?: Array<{ Date?: string; FullyPaidOnDate?: string; AmountPaid?: number; Total?: number }>;
    }>(`https://api.xero.com/api.xro/2.0/Invoices?where=${where}&order=FullyPaidOnDate DESC&page=${page}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'xero-tenant-id': tenantId,
        Accept: 'application/json',
      },
    });

    const rows = data.Invoices || [];
    if (rows.length === 0) {
      break;
    }

    let minPaidOnPage = Number.MAX_SAFE_INTEGER;
    for (const inv of rows) {
      const created = paidAtSeconds(inv);
      if (!created) {
        continue;
      }
      minPaidOnPage = Math.min(minPaidOnPage, created);
      if (created < fromTs || created > toTs) {
        continue;
      }
      const amount = inv.AmountPaid ?? inv.Total ?? 0;
      if (amount > 0) {
        out.push({ created, amount });
      }
    }

    if (minPaidOnPage < fromTs) {
      break;
    }
    if (rows.length < 100) {
      break;
    }
  }

  return out;
}

async function fetchLocalXeroInvoiced(fromTs: number, toTs: number): Promise<Array<{ created: number; amount: number }>> {
  if (!xeroAuth.isAuthenticated()) {
    await xeroAuth.ensureValidAccessToken().catch(() => undefined);
  }
  const token = xeroAuth.getAccessToken();
  const tenantId = xeroAuth.getTenantId();
  if (!token || !tenantId) {
    return [];
  }

  const out: Array<{ created: number; amount: number }> = [];
  const where = encodeURIComponent('Type=="ACCREC"&&(Status=="AUTHORISED"||Status=="SUBMITTED"||Status=="SENT")');

  for (let page = 1; page <= 30; page++) {
    const { data } = await axios.get<{
      Invoices?: Array<{ Date?: string; Total?: number }>;
    }>(`https://api.xero.com/api.xro/2.0/Invoices?where=${where}&order=Date DESC&page=${page}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'xero-tenant-id': tenantId,
        Accept: 'application/json',
      },
    });

    const rows = data.Invoices || [];
    if (rows.length === 0) {
      break;
    }

    let minDateOnPage = Number.MAX_SAFE_INTEGER;
    for (const inv of rows) {
      const ms = parseXeroDateMs(inv.Date);
      if (!ms) {
        continue;
      }
      const created = Math.floor(ms / 1000);
      minDateOnPage = Math.min(minDateOnPage, created);
      if (created < fromTs || created > toTs) {
        continue;
      }
      const amount = inv.Total ?? 0;
      if (amount > 0) {
        out.push({ created, amount });
      }
    }

    if (minDateOnPage < fromTs) {
      break;
    }
    if (rows.length < 100) {
      break;
    }
  }

  return out;
}

async function buildLocalPeriodReport(
  settings: AppSettings,
  from: Date,
  to: Date,
  granularity: Granularity,
  includeInvoiced: boolean
): Promise<IncomePeriodSnapshot> {
  const currency = (settings.integrations.stripe.billing?.defaultCurrency || 'aud').toUpperCase();
  const fromTs = Math.floor(from.getTime() / 1000);
  const toTs = Math.floor(new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59).getTime() / 1000);
  const keys = bucketKeys(from, to, granularity);
  const buckets = emptySeries(keys);

  const key = settings.integrations.stripe.secretKey.trim();
  let stripePaidRows: Array<{ created: number; amount: number }> = [];
  let stripeInvoicedRows: Array<{ created: number; amount: number }> = [];
  if (key) {
    stripePaidRows = await listPaidChargesInRange(key, fromTs, toTs);
    if (includeInvoiced) {
      stripeInvoicedRows = await listStripeInvoicesIssuedInRange(key, fromTs, toTs);
    }
  }

  for (const r of stripePaidRows) {
    const bucketKey = bucketKeyForTimestamp(r.created, keys, granularity);
    if (bucketKey && buckets[bucketKey]) {
      buckets[bucketKey].stripePaid! += r.amount / 100;
    }
  }
  for (const r of stripeInvoicedRows) {
    const bucketKey = bucketKeyForTimestamp(r.created, keys, granularity);
    if (bucketKey && buckets[bucketKey]) {
      buckets[bucketKey].stripeInvoiced! += r.amount / 100;
    }
  }

  let xeroPaidRows: Array<{ created: number; amount: number }> = [];
  let xeroInvoicedRows: Array<{ created: number; amount: number }> = [];
  if (settings.integrations.xero.clientId.trim()) {
    try {
      xeroPaidRows = await fetchLocalXeroPaid(fromTs, toTs);
      if (includeInvoiced) {
        xeroInvoicedRows = await fetchLocalXeroInvoiced(fromTs, toTs);
      }
    } catch {
      /* optional */
    }
  }

  for (const r of xeroPaidRows) {
    const bucketKey = bucketKeyForTimestamp(r.created, keys, granularity);
    if (bucketKey && buckets[bucketKey]) {
      buckets[bucketKey].xeroPaid! += r.amount;
    }
  }
  for (const r of xeroInvoicedRows) {
    const bucketKey = bucketKeyForTimestamp(r.created, keys, granularity);
    if (bucketKey && buckets[bucketKey]) {
      buckets[bucketKey].xeroInvoiced! += r.amount;
    }
  }

  const series = keys.map((k) => finalizePoint(buckets[k]));
  const stripePaidTotal = stripePaidRows.reduce((s, r) => s + r.amount, 0) / 100;
  const stripeInvoicedTotal = stripeInvoicedRows.reduce((s, r) => s + r.amount, 0) / 100;
  const xeroPaidTotal = xeroPaidRows.reduce((s, r) => s + r.amount, 0);
  const xeroInvoicedTotal = xeroInvoicedRows.reduce((s, r) => s + r.amount, 0);
  const metaCount = settings.integrations.meta.accounts.filter((a) => a.pageId.trim()).length;

  const totalPaid = stripePaidTotal + xeroPaidTotal;
  const totalInvoiced = stripeInvoicedTotal + xeroInvoicedTotal;

  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
    totalPaid,
    totalInvoiced,
    total: totalPaid,
    series,
    channels: [
      {
        channel: 'stripe',
        label: 'Stripe',
        amount: stripePaidTotal,
        invoicedAmount: stripeInvoicedTotal,
        currency,
        count: stripePaidRows.length,
        trendPct: null,
      },
      {
        channel: 'xero',
        label: 'Xero',
        amount: xeroPaidTotal,
        invoicedAmount: xeroInvoicedTotal,
        currency,
        count: xeroPaidRows.length,
        trendPct: null,
      },
      {
        channel: 'meta',
        label: 'Meta / Social',
        amount: 0,
        invoicedAmount: 0,
        currency,
        count: metaCount,
        trendPct: null,
      },
    ],
  };
}

function normalizeReport(raw: IncomeReport): IncomeReport {
  const series = (raw.series || []).map((row) =>
    finalizePoint({
      ...row,
      stripePaid: row.stripePaid ?? row.stripe ?? 0,
      xeroPaid: row.xeroPaid ?? row.xero ?? 0,
      stripeInvoiced: row.stripeInvoiced ?? 0,
      xeroInvoiced: row.xeroInvoiced ?? 0,
      meta: row.meta ?? 0,
      stripe: row.stripe ?? 0,
      xero: row.xero ?? 0,
      total: row.total ?? 0,
    })
  );
  const totalPaid = raw.totalPaid ?? raw.total ?? 0;
  return {
    ...raw,
    series,
    totalPaid,
    totalInvoiced: raw.totalInvoiced ?? 0,
    total: totalPaid,
    granularity: raw.granularity ?? 'day',
    compareMode: raw.compareMode ?? (raw.yoy ? 'prior_year' : 'none'),
    includeInvoiced: raw.includeInvoiced ?? false,
    comparison: raw.comparison ?? null,
    yoy: raw.yoy ?? null,
  };
}

export async function fetchIncomeReport(settings: AppSettings, query: IncomeReportQuery): Promise<IncomeReport> {
  const compare: CompareMode =
    query.compare ?? (query.yoy ? 'prior_year' : 'none');
  const granularity = query.granularity ?? 'day';
  const includeInvoiced = query.includeInvoiced ?? false;

  if (useBackendAuth) {
    const params = new URLSearchParams({
      from: query.from,
      to: query.to,
      granularity,
      compare,
    });
    if (includeInvoiced) {
      params.set('includeInvoiced', '1');
    }
    return normalizeReport(await apiFetch<IncomeReport>(`/api/reporting/income?${params.toString()}`));
  }

  const from = new Date(query.from);
  const to = new Date(query.to);
  from.setHours(0, 0, 0, 0);
  to.setHours(0, 0, 0, 0);
  if (from > to) {
    throw new Error('From date must be before to date');
  }

  const current = await buildLocalPeriodReport(settings, from, to, granularity, includeInvoiced);
  const currency = (settings.integrations.stripe.billing?.defaultCurrency || 'aud').toUpperCase();

  const base: IncomeReport = {
    ...current,
    currency,
    granularity,
    compareMode: compare,
    includeInvoiced,
    comparison: null,
    yoy: null,
  };

  if (compare === 'none') {
    return normalizeReport(base);
  }

  const priorRange =
    compare === 'prior_year'
      ? { from: shiftYearBack(from), to: shiftYearBack(to) }
      : shiftPriorPeriod(from, to);

  const prior = await buildLocalPeriodReport(settings, priorRange.from, priorRange.to, granularity, includeInvoiced);
  const priorByChannel = new Map(prior.channels.map((c) => [c.channel, c.amount]));

  current.channels = current.channels.map((c) => ({
    ...c,
    trendPct: trendPct(c.amount, priorByChannel.get(c.channel) ?? 0),
  }));

  return normalizeReport({
    ...current,
    currency,
    granularity,
    compareMode: compare,
    includeInvoiced,
    comparison: { mode: compare, ...prior },
    yoy: compare === 'prior_year' ? prior : null,
  });
}

export function dayCount(from: string, to: string): number {
  const a = new Date(from);
  const b = new Date(to);
  return Math.max(1, Math.ceil((b.getTime() - a.getTime()) / 86_400_000) + 1);
}

export function formatBucketLabel(iso: string, granularity: Granularity): string {
  try {
    const d = new Date(iso);
    if (granularity === 'month') {
      return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    }
    if (granularity === 'week') {
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    }
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  } catch {
    return iso.slice(5);
  }
}
